import { Bug, ChevronDown, ChevronUp, DoorOpen, User } from 'lucide-react'
import { memo, useMemo } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { openFeedbackDialog } from '@/helpers/sentry/open-feedback-dialog'
import useTranslator from '@/hooks/translation'
import { useUserQuery } from '@/hooks/user/use-user-query'
import { useAuthLogoutDialog } from '@/stores'
import { StringSizeFormatter } from '@/utils/formatters/string'

import { useUserDropdown } from '../hooks/use-user-dropdown'
import { ProfileOption } from './option'
import { UserProfileProps } from './types'

export const UserProfile = memo(
  ({ isSidebarOpen, theme = 'light' }: UserProfileProps) => {
    const { isDropdownOpen, toggleDropdown } = useUserDropdown()
    const { onOpenChange } = useAuthLogoutDialog()

    const { getTranslation } = useTranslator()

    const { data: user } = useUserQuery()

    const username = StringSizeFormatter(user.name || '')

    const userOptions = useMemo(() => {
      return [
        {
          title: getTranslation('components.menu.reportIssue'),
          icon: <Bug />,
          onClick: async () => openFeedbackDialog(),
        },
        {
          title: getTranslation('components.menu.logout'),
          icon: <DoorOpen />,
          onClick: () => onOpenChange(true),
        },
      ]
    }, [getTranslation, onOpenChange])

    return (
      <div className="user-profile">
        <Button
          variant={theme === 'dark' ? 'botEnvironment' : 'ghost'}
          size="sm"
          className="h-16 w-full justify-between overflow-hidden rounded-none border-t px-4"
          onClick={toggleDropdown}
          aria-expanded={isDropdownOpen}
          aria-controls="user-options-dropdown"
        >
          <div className="flex items-center gap-2">
            <Avatar className="h-10 w-10">
              <AvatarImage
                src={'/images/icons/fintalk-logo-icon.svg'}
                alt="User avatar"
              />

              <AvatarFallback>
                <User />
              </AvatarFallback>
            </Avatar>

            <span className="text-sm">{isSidebarOpen && username}</span>
          </div>

          {isDropdownOpen ? <ChevronUp /> : <ChevronDown />}
        </Button>

        {isDropdownOpen && (
          <ul id="user-options-dropdown" className="flex flex-col gap-2">
            {userOptions.map((option) => (
              <ProfileOption
                key={option.title}
                item={option}
                isSidebarOpen={isSidebarOpen}
                theme={theme}
              />
            ))}
          </ul>
        )}
      </div>
    )
  },
)

UserProfile.displayName = 'UserProfile'
